<template>
  <form class="modal-card week-planner" @submit.prevent="save" @keydown.enter.prevent="">
    <header class="modal-card-head">
      <p class="modal-card-title">
        {{ $t('pages.production_data.employee_schedule.week.planning') }}
        <span v-if="plant" class="week-planner-subtitle">{{ plant.name }}</span>
        <span class="week-planner-subtitle">{{ weekLabel }}</span>
      </p>
    </header>
    <section class="modal-card-body">
      <b-field grouped group-multiline>
        <b-field :label="$t('pages.production_data.employee_schedule.week.add_employee')">
          <multiselect
            v-model="employeeToAdd"
            :options="addableEmployees"
            :multiple="false"
            :close-on-select="true"
            :loading="isLoading"
            :placeholder="$t('pages.production_data.employee_schedule.week.add_employee')"
            :custom-label="(employee) => fullName(employee)"
            track-by="id"
            style="min-width: 320px;"
            @input="addEmployee"
          />
        </b-field>
      </b-field>
      <div v-if="isLoading" class="has-text-centered py-4">
        <b-icon icon="arrows-rotate" custom-class="fa-spin" />
      </div>
      <p v-else-if="rows.length === 0" class="has-text-grey py-4">
        {{ $t('pages.production_data.employee_schedule.week.no_rows') }}
      </p>
      <div v-else class="week-grid-wrapper">
        <table class="week-grid">
          <thead>
            <tr>
              <th class="week-grid-name">
                {{ $t('pages.production_data.employee_schedule.week.employee') }}
              </th>
              <th class="week-grid-pattern">
                {{ $t('pages.production_data.employee_schedule.week.pattern') }}
              </th>
              <th
                v-for="day in days"
                :key="day.key"
                :class="{ 'week-grid-weekend': day.weekend, 'week-grid-today': day.today }"
              >
                <div class="week-grid-day">
                  {{ day.weekday }}
                </div>
                <div class="week-grid-date">
                  {{ day.short }}
                </div>
              </th>
              <th class="week-grid-total">
                {{ $t('pages.production_data.employee_schedule.week.total') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.employeeId">
              <td class="week-grid-name">
                <div class="week-grid-employee">
                  <span>{{ row.name }}</span>
                  <a
                    class="week-grid-remove"
                    :title="$t('pages.production_data.employee_schedule.week.remove_row')"
                    @click="removeRow(row)"
                  >
                    <b-icon icon="xmark" size="is-small" />
                  </a>
                </div>
              </td>
              <td class="week-grid-pattern">
                <div class="select is-small is-fullwidth">
                  <select v-model="row.pattern.shift">
                    <option v-for="shift in shifts" :key="shift" :value="shift">
                      {{ shift }}
                    </option>
                  </select>
                </div>
                <div class="select is-small is-fullwidth mt-1">
                  <select v-model="row.pattern.activity">
                    <option v-for="activity in activities" :key="activity.key" :value="activity.key">
                      {{ $t(activity.labelKey) }}
                    </option>
                  </select>
                </div>
                <input
                  v-model="row.pattern.hours"
                  class="input is-small mt-1"
                  type="number"
                  min="0"
                  max="12"
                  step="0.25"
                >
                <div class="buttons are-small mt-1">
                  <button type="button" class="button is-small" @click="fillWeekdays(row)">
                    {{ $t('pages.production_data.employee_schedule.week.fill_weekdays') }}
                  </button>
                  <button type="button" class="button is-small" @click="clearRow(row)">
                    {{ $t('pages.production_data.employee_schedule.week.clear') }}
                  </button>
                </div>
              </td>
              <td
                v-for="(cell, day) in row.days"
                :key="days[day].key"
                :class="{
                  'week-grid-weekend': days[day].weekend,
                  'week-grid-today': days[day].today,
                  'week-grid-changed': isDirty(cell),
                  'week-grid-invalid': isIncomplete(cell)
                }"
              >
                <div class="select is-small is-fullwidth">
                  <select :value="cell.shift" @change="setShift(row, cell, $event.target.value)">
                    <option value="">
                      {{ $t('pages.production_data.employee_schedule.week.not_working') }}
                    </option>
                    <option v-for="shift in shifts" :key="shift" :value="shift">
                      {{ shift }}
                    </option>
                  </select>
                </div>
                <template v-if="cell.shift">
                  <div class="select is-small is-fullwidth mt-1">
                    <select :value="cell.activity" @change="setActivity(row, cell, $event.target.value)">
                      <option v-for="activity in activities" :key="activity.key" :value="activity.key">
                        {{ $t(activity.labelKey) }}
                      </option>
                    </select>
                  </div>
                  <input
                    v-if="!isAbsence(cell.activity)"
                    v-model="cell.hours"
                    class="input is-small mt-1"
                    type="number"
                    min="0"
                    max="12"
                    step="0.25"
                  >
                  <span
                    v-if="cell.entries > 1"
                    class="week-grid-multi"
                    :title="$t('pages.production_data.employee_schedule.week.multi_entry', { n: cell.entries })"
                  >
                    {{ $t('pages.production_data.employee_schedule.week.multi_flag', { n: cell.entries }) }}
                  </span>
                </template>
              </td>
              <td class="week-grid-total">
                {{ rowTotal(row) }}
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <th class="week-grid-name">
                {{ $t('pages.production_data.employee_schedule.week.day_total') }}
              </th>
              <th class="week-grid-pattern" />
              <th
                v-for="(total, day) in dayTotals"
                :key="days[day].key"
                :class="{ 'week-grid-weekend': days[day].weekend, 'week-grid-today': days[day].today }"
              >
                {{ total }}
              </th>
              <th class="week-grid-total">
                {{ weekTotal }}
              </th>
            </tr>
          </tfoot>
        </table>
      </div>
      <p v-if="incompleteCells > 0" class="week-planner-invalid">
        {{ $t('pages.production_data.employee_schedule.week.invalid_hours') }}
      </p>
      <p class="week-planner-note">
        {{ $t('pages.production_data.employee_schedule.week.replaces_day') }}
      </p>
    </section>
    <footer class="modal-card-foot">
      <button
        type="submit"
        class="button is-info is-fullwidth"
        :class="{ 'is-loading': isSaving }"
        :disabled="isLoading || isSaving || incompleteCells > 0 || dirtyCells.length === 0"
      >
        {{ $t('global.submit') }}
        <span v-if="dirtyCells.length > 0">&nbsp;({{ dirtyCells.length }})</span>
      </button>
    </footer>
  </form>
</template>

<script>
import EmployeeSchedule from '@/services/v3/employeeSchedule'
import saving from '@/mixins/saving'
import {
  WEEK_SHIFTS,
  WEEK_ACTIVITIES,
  buildWeekCells,
  cellHours,
  dateKey,
  dateLocale,
  emptyCell,
  employeeName,
  employeeSortKey,
  isAbsenceActivity,
  isCellDirty,
  isCellIncomplete,
  isoWeekNumber,
  patternFromLastShift,
  roundHours,
  weekDates,
  worksDuringWeek
} from '@/helpers/weekSchedule'

export default {
  name: 'WeekSchedulePlanner',
  mixins: [saving],
  props: {
    plant: {
      type: Object,
      required: true
    },
    weekStart: {
      type: Date,
      required: true
    }
  },
  data () {
    return {
      isLoading: false,
      employees: [],
      rows: [],
      employeeToAdd: null,
      shifts: WEEK_SHIFTS,
      activities: WEEK_ACTIVITIES
    }
  },
  computed: {
    dates () {
      return weekDates(this.weekStart)
    },
    locale () {
      return dateLocale(this.$i18n.locale)
    },
    days () {
      const today = dateKey(new Date())
      return this.dates.map((date) => {
        return {
          key: dateKey(date),
          weekday: date.toLocaleDateString(this.locale, { weekday: 'short' }),
          short: date.toLocaleDateString(this.locale, { day: '2-digit', month: '2-digit' }),
          weekend: date.getDay() === 0 || date.getDay() === 6,
          today: dateKey(date) === today
        }
      })
    },
    weekLabel () {
      const range = this.$t('pages.production_data.employee_schedule.week.range', {
        start: this.dates[0].toLocaleDateString(this.locale, { day: 'numeric', month: 'long' }),
        end: this.dates[6].toLocaleDateString(this.locale, { day: 'numeric', month: 'long', year: 'numeric' })
      })
      return this.$t('pages.production_data.employee_schedule.week.label', { n: isoWeekNumber(this.weekStart) }) + ' — ' + range
    },
    addableEmployees () {
      const planned = this.rows.map((row) => row.employeeId)
      return this.employees.filter((employee) => {
        return !planned.includes(employee.id) && worksDuringWeek(employee, this.dates)
      })
    },
    dirtyCells () {
      const dirty = []
      this.rows.forEach((row) => {
        row.days.forEach((cell, day) => {
          if (this.isDirty(cell)) {
            dirty.push({ employeeId: row.employeeId, date: this.days[day].key, cell })
          }
        })
      })
      return dirty
    },
    incompleteCells () {
      return this.rows.reduce((count, row) => {
        return count + row.days.filter((cell) => this.isIncomplete(cell)).length
      }, 0)
    },
    dayTotals () {
      return this.days.map((day, index) => {
        return this.roundHours(this.rows.reduce((sum, row) => sum + this.cellHours(row.days[index]), 0))
      })
    },
    weekTotal () {
      return this.roundHours(this.dayTotals.reduce((sum, total) => sum + total, 0))
    }
  },
  mounted () {
    this.load()
  },
  methods: {
    fullName: employeeName,
    isAbsence: isAbsenceActivity,
    cellHours,
    roundHours,
    isDirty: isCellDirty,
    isIncomplete: isCellIncomplete,
    async load () {
      this.isLoading = true
      try {
        const data = await EmployeeSchedule.getTable({
          siteId: this.plant.id,
          start: this.days[0].key,
          end: this.days[6].key
        })
        this.employees = [...data.employees].sort((a, b) => employeeSortKey(a).localeCompare(employeeSortKey(b)))
        const cellsByEmployee = buildWeekCells(data.schedules, this.dates)
        this.rows = this.employees
          .filter((employee) => cellsByEmployee.has(employee.id))
          .map((employee) => this.buildRow(employee, cellsByEmployee.get(employee.id)))
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
      }
      this.isLoading = false
    },
    buildRow (employee, cells) {
      const days = (cells || this.days.map(() => emptyCell())).map((cell) => {
        return { ...cell, original: { shift: cell.shift, activity: cell.activity, hours: cell.hours } }
      })
      // The week already worked is a better starting pattern than the last one.
      const filled = days.find((day) => day.shift)
      return {
        employeeId: employee.id,
        name: employeeName(employee),
        pattern: filled
          ? { shift: filled.shift, activity: filled.activity, hours: filled.hours }
          : patternFromLastShift(employee),
        days
      }
    },
    addEmployee (employee) {
      if (!employee) {
        return
      }
      this.rows.push(this.buildRow(employee, null))
      this.rows.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
      this.employeeToAdd = null
    },
    removeRow (row) {
      this.rows = this.rows.filter((candidate) => candidate !== row)
    },
    setShift (row, cell, shift) {
      if (!shift) {
        cell.shift = null
        cell.activity = null
        cell.hours = 0
        return
      }
      // A day being switched on inherits the row pattern; an existing day only
      // changes shift.
      if (!cell.shift) {
        cell.activity = row.pattern.activity
        cell.hours = this.isAbsence(row.pattern.activity) ? 0 : this.roundHours(row.pattern.hours)
      }
      cell.shift = shift
    },
    setActivity (row, cell, activity) {
      // An absence carries no hours, and coming back out of one has to restore
      // them or the day would be saved as worked-but-empty.
      if (this.isAbsence(activity)) {
        cell.hours = 0
      } else if (this.isAbsence(cell.activity) || this.cellHours(cell) === 0) {
        cell.hours = this.roundHours(row.pattern.hours) || this.roundHours(cell.original.hours)
      }
      cell.activity = activity
    },
    fillWeekdays (row) {
      row.days.forEach((cell, day) => {
        if (this.days[day].weekend) {
          return
        }
        cell.shift = row.pattern.shift
        cell.activity = row.pattern.activity
        cell.hours = this.isAbsence(row.pattern.activity) ? 0 : this.roundHours(row.pattern.hours)
      })
    },
    clearRow (row) {
      row.days.forEach((cell) => {
        cell.shift = null
        cell.activity = null
        cell.hours = 0
      })
    },
    rowTotal (row) {
      return this.roundHours(row.days.reduce((sum, cell) => sum + this.cellHours(cell), 0))
    },
    async save () {
      const dirty = this.dirtyCells
      if (dirty.length === 0 || this.incompleteCells > 0) {
        return
      }
      // Each edited day is replaced as a whole. The week goes in one transaction, so
      // on a failure nothing is written and the grid keeps the edits for a retry.
      const archives = dirty.map(change => ({ id: change.employeeId, date: change.date }))
      const inserts = dirty.filter(change => change.cell.shift).map(change => ({
        siteId: this.plant.id,
        date: change.date,
        activity: change.cell.activity,
        hours: this.cellHours(change.cell),
        shift: change.cell.shift,
        id_employee: change.employeeId
      }))
      if (!await this.runSave(() => EmployeeSchedule.replaceSchedules({ siteId: this.plant.id, archives, inserts }))) {
        return
      }
      this.$buefy.toast.open({
        message: this.$t('pages.production_data.employee_schedule.week.saved'),
        duration: 3000,
        type: 'is-success'
      })
      this.$emit('saved')
    }
  }
}
</script>

<style scoped>

.week-planner {
  min-width: 60vw;
  width: fit-content;
}

.week-planner-subtitle {
  font-size: 1rem;
  font-weight: normal;
  margin-left: 0.75rem;
}

.week-planner-note {
  color: rgb(125, 125, 125);
  font-size: 0.8rem;
  margin-top: 0.75rem;
}

.week-grid-wrapper {
  overflow-x: auto;
}

.week-grid {
  border-collapse: collapse;
  width: 100%;
}

.week-grid th,
.week-grid td {
  border: 1px rgb(190, 190, 190) solid;
  padding: 0.25rem;
  text-align: center;
  vertical-align: top;
}

.week-grid thead th {
  background-color: rgba(190, 230, 250, 1);
  vertical-align: middle;
}

.week-grid tfoot th {
  background-color: rgba(235, 235, 235, 1);
  vertical-align: middle;
}

.week-grid td {
  min-width: 6.5rem;
}

.week-grid-name {
  min-width: 12rem;
  text-align: left !important;
  vertical-align: middle !important;
}

.week-grid-pattern {
  min-width: 8.5rem;
  background-color: rgba(248, 248, 248, 1);
}

.week-grid-total {
  min-width: 4rem;
  font-weight: bold;
  vertical-align: middle !important;
}

.week-grid-day {
  text-transform: capitalize;
}

.week-grid-date {
  font-size: 0.75rem;
  font-weight: normal;
}

.week-grid-weekend {
  background-color: rgba(240, 240, 240, 1);
}

.week-grid-today {
  box-shadow: inset 0 3px 0 0 rgba(110, 150, 170, 1);
}

.week-grid-changed {
  background-color: rgba(255, 250, 205, 1);
}

.week-grid-invalid {
  background-color: rgba(255, 225, 225, 1);
}

.week-planner-invalid {
  color: rgb(210, 80, 80);
  font-size: 0.85rem;
  margin-top: 0.75rem;
}

.week-grid-employee {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.week-grid-remove {
  color: rgb(150, 150, 150);
}

.week-grid-remove:hover {
  color: rgb(210, 80, 80);
}

.week-grid-multi {
  color: rgb(210, 130, 30);
  cursor: help;
  display: block;
  font-size: 0.7rem;
}

</style>
